import { useState, useEffect, useRef } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';
import '../styles/Gallery.css';

export default function Gallery() {
  const { t } = useLanguage();

  const images = Array.from({ length: 42 }, (_, i) => ({
    id: i + 1,
    img: `/images/${i + 1}.jpg`
  }));

  const [index, setIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [cardWidth, setCardWidth] = useState(336);
  const [isPaused, setIsPaused] = useState(false);
  const timeoutRef = useRef(null);
  const delay = 3000;

  const extendedImages = [...images, ...images, ...images];

  useEffect(() => {
    const updateCardWidth = () => {
      const width = window.innerWidth;
      if (width <= 360) {
        setCardWidth(175 + 12);
      } else if (width <= 480) {
        setCardWidth(200 + 14);
      } else if (width <= 640) {
        setCardWidth(220 + 16);
      } else if (width <= 768) {
        setCardWidth(240 + 16);
      } else if (width <= 1024) {
        setCardWidth(260 + 20);
      } else if (width <= 1200) {
        setCardWidth(280 + 24);
      } else {
        setCardWidth(320 + 24);
      }
    };

    updateCardWidth();
    window.addEventListener('resize', updateCardWidth);
    return () => window.removeEventListener('resize', updateCardWidth);
  }, []);

  useEffect(() => {
    resetTimeout();
    if (!isPaused) {
      timeoutRef.current = setTimeout(() => {
        setIndex((prevIndex) => prevIndex + 1);
      }, delay);
    }
    return () => resetTimeout();
  }, [index, isPaused]);

  useEffect(() => {
    if (index >= images.length * 2) {
      setTimeout(() => {
        setIsTransitioning(false);
        setIndex(images.length);
      }, 600);
    } else if (index === 0) {
      setTimeout(() => {
        setIsTransitioning(false);
        setIndex(images.length);
      }, 600);
    } else {
      if (!isTransitioning) setIsTransitioning(true);
    }
  }, [index, isTransitioning, images.length]);

  const resetTimeout = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const handleNext = () => setIndex((prev) => prev + 1);
  const handlePrev = () => setIndex((prev) => prev - 1);

  const getTransform = () => {
    const offset = index * cardWidth;
    return `translateX(calc(50vw - ${offset}px - ${cardWidth / 2}px))`;
  };

  return (
    <section className="gallery-section" id="gallery">
      <div className="gallery-header">
        <span className="gallery-badge">{t('gallery.subtitle')}</span>
        <h2 className="gallery-title">{t('gallery.title')}</h2>
      </div>

      <div
        className="gallery-viewport"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={() => setIsPaused(true)}
        onTouchEnd={() => setIsPaused(false)}
      >
        <div className="gallery-overlay overlay-left"></div>
        <div className="gallery-overlay overlay-right"></div>

        <button className="nav-btn prev-btn" onClick={handlePrev} aria-label="Précédent">
          <FiChevronLeft size={28} />
        </button>
        <button className="nav-btn next-btn" onClick={handleNext} aria-label="Suivant">
          <FiChevronRight size={28} />
        </button>

        <div
          className="gallery-track"
          style={{
            transform: getTransform(),
            transition: isTransitioning ? 'transform 0.8s cubic-bezier(0.2, 0.8, 0.2, 1)' : 'none'
          }}
        >
          {extendedImages.map((item, idx) => {
            const isActive = idx === index;

            return (
              <div
                key={`${item.id}-${idx}`}
                className={`gallery-card ${isActive ? 'active' : ''}`}
              >
                <div className="card-inner">
                  <img
                    src={item.img}
                    alt=""
                    loading="lazy"
                    draggable="false"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/images/1.jpg";
                    }}
                  />
                  <div className="card-glow"></div>
                </div>

                {isActive && (
                  <div className={`progress-bar ${isPaused ? 'paused' : ''}`}></div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}